<!--
  ArtworkReplaceModal -- the Decoration Library artwork page's "Replace
  artwork" (Bug Intake #297: the button did nothing).

  Replace here is the SAME operation as the product editor's Replace, only
  with the artwork as the starting point instead of a placement card
  (Jake, 2026-10-06: Replace REBINDS, then asks the scope):

    1. pick a file -> it is uploaded as a NEW library artwork in the same
       repository (the old artwork is never edited in place or deleted: it
       stays in the library for anything still using it);
    2. GET /api/artworks/<old>/rebind_scope?new_artwork_id=<new> says where
       the old artwork is used, what it may cost, and which live stores need
       the client's approval first;
    3. on confirm, POST /api/artworks/<old>/rebind {scope: 'all'} re-points
       every use. A live store keeps its current decoration until the client
       approves the new one (placement_rebind's "LIVE STORES NEED APPROVAL
       FIRST"), which is how "reset approved status if necessary" is met:
       the new art is never silently approved.

  An artwork that is not used on any product has nothing to rebind; the new
  file is simply in the library and the user is offered it.

  The host owns navigation: this component emits 'replaced' with the new
  artwork and the rebind result (or null when nothing was rebound) and
  'close'.
-->
<template>
    <div
        class="arm-backdrop"
        data-testid="dla-replace-modal"
        @click.self="onCancel"
    >
        <div class="arm" role="dialog" aria-modal="true">
            <header class="arm__header">
                <h3 class="arm__title">Replace artwork</h3>
                <button
                    type="button"
                    class="arm__close"
                    data-testid="dla-replace-cancel"
                    aria-label="Close"
                    @click="onCancel"
                >
                    &times;
                </button>
            </header>

            <div class="arm__body">
                <template v-if="phase === 'unused'">
                    <p data-testid="dla-replace-unused">
                        &ldquo;{{ oldName }}&rdquo; is not on any product yet,
                        so there is nothing to replace. Your new file
                        &ldquo;{{ newName }}&rdquo; is in the library.
                    </p>
                    <div class="arm__footer">
                        <button
                            type="button"
                            class="arm__btn arm__btn--primary"
                            data-testid="dla-replace-open-new"
                            @click="emitReplaced(null)"
                        >
                            Open the new artwork
                        </button>
                    </div>
                </template>

                <template v-else-if="phase === 'held'">
                    <p data-testid="dla-replace-held">
                        Replaced. {{ heldText }}
                    </p>
                    <div class="arm__footer">
                        <button
                            type="button"
                            class="arm__btn arm__btn--primary"
                            data-testid="dla-replace-open-new"
                            @click="emitReplaced(heldResult)"
                        >
                            Open the new artwork
                        </button>
                    </div>
                </template>

                <template v-else>
                    <p class="arm__lede">
                        Choose the new file. It is added to your library as a
                        new artwork; &ldquo;{{ oldName }}&rdquo; is kept.
                    </p>
                    <input
                        type="file"
                        :accept="acceptTypes"
                        :disabled="busy"
                        data-testid="dla-replace-file"
                        @change="onFileChange"
                    />
                    <p
                        v-if="error"
                        class="arm__error"
                        data-testid="dla-replace-error"
                    >
                        {{ error }}
                    </p>
                    <div class="arm__footer">
                        <button
                            type="button"
                            class="arm__btn"
                            :disabled="busy"
                            @click="onCancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="arm__btn arm__btn--primary"
                            :disabled="busy"
                            data-testid="dla-replace-upload"
                            @click="onUpload"
                        >
                            {{ busy ? 'Working...' : 'Upload and continue' }}
                        </button>
                    </div>
                </template>
            </div>
        </div>

        <ReplaceScopeDialog
            v-if="scopeOpen"
            v-model="scopeOpen"
            :old-artwork-name="oldName"
            :new-artwork-name="newName"
            :scope="scope"
            :loading="false"
            :error="null"
            every-product-only
            @every-product="onConfirm"
            @cancel="onScopeCancel"
        />
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import axios from '@/axios'
import { useArtworksStore } from '@/stores/artworks'
import {
    ARTWORK_UPLOAD_ACCEPT,
    validateArtworkFile,
} from '@/utils/artworkUploadContract'
import { formatArtworkName } from '@/utils/formatArtworkName'
import ReplaceScopeDialog from '@/pages/distributor/store-editor/StoreProducts/Editor/ReplaceScopeDialog.vue'

const props = defineProps({
    // The artwork being replaced: the detail header ({id, repository_id, ...}).
    artwork: { type: Object, required: true },
    // Its display name (the page already derived it).
    oldName: { type: String, required: true },
})
const emit = defineEmits(['replaced', 'close'])

const acceptTypes = ARTWORK_UPLOAD_ACCEPT

const file = ref(null)
const busy = ref(false)
const error = ref(null)
// 'pick' | 'unused' | 'held'
const phase = ref('pick')
const created = ref(null)
const scope = ref(null)
const scopeOpen = ref(false)
const heldResult = ref(null)
// The File that `created` was uploaded from: a retry of the same file after
// a failed later step reuses the artwork instead of orphaning another.
const uploadedFile = ref(null)

const heldText = computed(() => {
    const stores = heldResult.value?.held?.stores || []
    const names = stores.map((s) => s.name).filter(Boolean).join(', ')
    return (
        `${names || 'A live store'} keeps showing the current art until `
        + 'the client approves the new one.'
    )
})

const newName = computed(
    () =>
        formatArtworkName(created.value?.filename || file.value?.name)
        || 'the new artwork',
)

function onFileChange(evt) {
    file.value = evt?.target?.files?.[0] ?? null
    error.value = file.value ? validateArtworkFile(file.value).error : null
}

function message(err, fallback) {
    return err?.response?.data?.error || err?.message || fallback
}

async function onUpload() {
    if (busy.value) return
    const check = validateArtworkFile(file.value)
    if (!check.ok) {
        error.value = check.error
        return
    }
    if (props.artwork.repository_id == null) {
        error.value = 'Could not find this artwork’s repository. Reload and try again.'
        return
    }
    busy.value = true
    error.value = null
    try {
        // Reuse the one upload path every library surface shares.
        if (!created.value || uploadedFile.value !== file.value) {
            created.value = await useArtworksStore().uploadArtwork({
                file: file.value,
                filename: file.value.name,
                repositoryId: props.artwork.repository_id,
            })
            uploadedFile.value = file.value
        }
        const resp = await axios.get(
            `/api/artworks/${props.artwork.id}/rebind_scope`
            + `?new_artwork_id=${encodeURIComponent(created.value.id)}`,
        )
        scope.value = resp?.data ?? null
        if (!scope.value || Number(scope.value.product_count) === 0) {
            phase.value = 'unused'
            return
        }
        scopeOpen.value = true
    } catch (err) {
        error.value = message(err, 'Could not upload the new file. Try again.')
    } finally {
        busy.value = false
    }
}

async function onConfirm() {
    busy.value = true
    error.value = null
    try {
        const resp = await axios.post(`/api/artworks/${props.artwork.id}/rebind`, {
            new_artwork_id: Number(created.value.id),
            scope: 'all',
        })
        const result = resp?.data ?? {}
        const held = result.held || {}
        if (Number(held.product_count) > 0 || (held.stores || []).length > 0) {
            heldResult.value = result
            scopeOpen.value = false
            phase.value = 'held'
            return
        }
        emitReplaced(result)
    } catch (err) {
        // The new file is already in the library; say so, so a retry does
        // not read as "nothing happened".
        error.value =
            `${message(err, 'Could not replace the artwork.')} `
            + `The new file “${newName.value}” is in your library; `
            + 'nothing on your products changed.'
    } finally {
        busy.value = false
    }
}

function onScopeCancel() {
    scopeOpen.value = false
}

function emitReplaced(result) {
    emit('replaced', { artwork: created.value, result })
}

function onCancel() {
    if (busy.value) return
    emit('close')
}
</script>

<style scoped>
.arm-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.arm {
    background: var(--tf-bg, #fff);
    border-radius: 8px;
    width: min(480px, 92vw);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
}
.arm__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.arm__title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}
.arm__close {
    background: none;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.6);
}
.arm__body {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.arm__lede {
    margin: 0;
    font-size: 14px;
}
.arm__error {
    color: #d32f2f;
    font-size: 13px;
    margin: 0;
}
.arm__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
.arm__btn {
    padding: 6px 14px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: #fff;
    cursor: pointer;
    font-size: 14px;
}
.arm__btn--primary {
    background: #1976d2;
    color: #fff;
    border-color: #1976d2;
}
.arm__btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
